import { $cookie } from "@/cookie";
import { guid, isEqual } from "@/utils/util";

export const COOKIE_KEYS = {
  ANONYMOUS_ID: "ai_anonymous_id",
  SESSION_ID: "ai_session_id",
};

export interface UserState {
  cookieId: string;
  sess: string;
  mobile: string;
  email: string;
  name: string;
}

interface AddUserState {
  _version: number;
}

class UserStateManager {
  private state: UserState & AddUserState = {
    cookieId: "",
    sess: "",
    mobile: "",
    email: "",
    name: "",
    _version: 0,
  };

  private listeners: Array<(state: UserState) => void> = [];

  constructor() {
    // 初始化 cookieId
    this.initializeCookieId();
  }

  private initializeCookieId(): void {
    const cid: string | null = $cookie.get(COOKIE_KEYS.ANONYMOUS_ID);
    const cookieId = cid == null || cid == "" ? guid() : cid;
    if (typeof cid == "string") {
      this.state.cookieId = cookieId;
    }
  }

  get snapshot(): Readonly<UserState> {
    return Object.freeze({ ...this.state });
  }

  setState(
    update: Partial<UserState> | ((prev: UserState) => Partial<UserState>),
    options: {
      emitEvent?: boolean;
      deepCompare?: boolean;
    } = {},
  ): void {
    const { emitEvent = true, deepCompare = false } = options;

    const newPartialState =
      typeof update === "function" ? update(this.state) : update;

    // 深度比较优化
    if (deepCompare && isEqual(this.state, newPartialState)) {
      return;
    }

    this.state = { ...this.state, ...newPartialState };
    this.state._version = (this.state._version || 0) + 1;

    if (emitEvent) {
      this.notifyListeners();
    }
  }

  subscribe(listener: (state: UserState) => void): () => void {
    this.listeners.push(listener);
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach((listener) => listener(this.snapshot));
  }

  // 获取特定状态
  getState<K extends keyof UserState>(key: K): UserState[K] {
    return this.state[key];
  }

  // 批量更新状态
  batchUpdate(updates: Array<Partial<UserState>>): void {
    const finalUpdate = updates.reduce(
      (acc, update) => ({ ...acc, ...update }),
      {},
    );
    this.setState(finalUpdate);
  }
}

export { UserStateManager };
